/**
 * 3d-tiles-renderer requests child tiles using paths it discovers in tileset
 * JSON. Those URLs are pre-signed FileService URLs (the Splats API
 * `downloadUrl` wildcard and `tilesetUrl`), not Splats API operations, so a
 * generated data-access client cannot issue them. This module rewrites the
 * renderer's fetches onto the signed template.
 */
import { sanitizeTilesetJsonText } from './tileset-json-sanitizer';
import {
  isRequestForTileset,
  isTilesetJsonRequest,
  rewriteTrimbleTileUrl,
  TRIMBLE_HOST,
} from './trimble-tile-url';

async function maybeSanitizeTilesetResponse(
  response: Response,
  url: string,
  tilesetUrl: string
): Promise<Response> {
  if (!response.ok || !isTilesetJsonRequest(url, tilesetUrl)) {
    return response;
  }

  const text = await response.text();
  return new Response(sanitizeTilesetJsonText(text), {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  });
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') {
    return input;
  }
  return input instanceof URL ? input.href : input.url;
}

type TrimbleTileFetch = (
  input: RequestInfo | URL,
  init?: RequestInit
) => Promise<Response>;

/**
 * Fetch for `GaussianSplattingPlugin.loadTiles({ customFetch })` that signs
 * 3D Tiles requests of one tileset with flat FileService URLs. Global `fetch`
 * is resolved per request so interceptors installed by the host still apply.
 */
// Wider than web3d's string-only CustomFetchFn (and still assignable to it)
// because 3d-tiles-renderer's fetchData can pass a URL at runtime.
export function createTrimbleTileFetch(
  tilesetUrl: string,
  templateUrl?: string
): TrimbleTileFetch {
  return async (input, init) => {
    const url = requestUrl(input);
    if (!url.includes(TRIMBLE_HOST) || !isRequestForTileset(url, tilesetUrl)) {
      return globalThis.fetch(input, init);
    }

    const rewritten = rewriteTrimbleTileUrl(url, tilesetUrl, templateUrl);
    const request =
      input instanceof Request ? new Request(rewritten, input) : rewritten;
    const response = await globalThis.fetch(request, init);
    return maybeSanitizeTilesetResponse(response, rewritten, tilesetUrl);
  };
}
